// Graph components
import Legend from './component/Legend'
import GraphPoint from './component/GraphPoint'
import Tooltip from './component/Tooltip'

// Global styles
import style from './config/style'

// Utility functions
import formatDate from './util/format-date'
import formatDateTime from './util/format-date-time'
import getOffset from './util/get-offset'

const emptyTooltip = []
const hiddenTooltip = { display: 'none' }

/**
 * @define		Graph
 * @desc 		The common definition for charting components.
 */
export default {

	props: {
		/**
		 * @prop 	data
		 * @type 	Array
		 * @desc	An array of values to be rendered in this chart.
		 * @required
		 */
	    data: {
	        type: Array,
	        required: true
	    },
		/**
		 * @prop 	startDate
		 * @type 	Date
		 * @desc	The minimum date that should be displayed in the chart.
		 * @required
		 */
	    startDate: {
	        type: Date,
	        required: true
	    },
		/**
		 * @prop  	date
		 * @type 	Date
		 * @desc	The maximum date that should be displayed in the chart.
		 * @required
		 */
	    date: {
	        type: Date,
	        required: true
	    },
		/**
		 * @prop  	width
		 * @type 	Number
		 * @desc	The width of the chart in pixels.
		 * @default 1000
		 */
	    width: {
	        type: Number,
	        default: 1000
	    },
		/**
		 * @prop  	height
		 * @type 	Number
		 * @desc	The height of the chart in pixels.
		 * @default 200
		 */
	    height: {
	        type: Number,
	        default: 200
		},
		/**
		 * @prop  	binWidth
		 * @type 	Number
		 * @desc	The number of days to include in each bin. The maximum granularity is always 1 day.
		 * @default 200
		 */
		binWidth: {
	        type: Number,
	        default: 1
	    },
		/**
		 * @prop  	unit
		 * @type 	String
		 * @desc	A display unit showed in the tooltip for hoverable charts.
		 */
		unit: {
			type: String
		}
	},

	/**
	 * @function 	data
	 */
	data() {
		return {
			// Global styles that are applied to all charts
			style,
			// Global reference to number of milliseconds in a day
			oneDay: 86400000,
			// The bin that should be rendered in the tooltip (default empty array)
			tooltipBin: emptyTooltip,
			// Reference to current bin being rendered into the tooltip
			currentTooltipBin: null,
			tooltipDisplay: hiddenTooltip,
			tooltipHideTimeout: null
		}
	},

	computed: {

		/**
		 * @function 	binInterval
		 * @desc 		Returns the interval of time for each bin in milliseconds.
		 * @return 		{Number} bin length in milliseconds
		 */
		binInterval() {
			return this.oneDay * this.binWidth
		},

		/**
		 * @function 	interval
		 * @desc 		Returns the total number of milliseconds between the start and end times.
		 * @return 		{Number} total chart timespan in milliseconds
		 */
		interval() {
			return this.date.getTime() - this.startDate.getTime()
		},

		/**
		 * @function 	legendStart
		 * @desc 		Returns the y position where the chart's legend text begins.
		 * @return 		{Number} legend start in pixels
		 */
		legendStart() {
			return this.height - this.style.padding * 2 - this.style.legendHeight
		},

		displayHeight() {
			return this.height - this.style.padding * 2 - this.style.legendHeight - 70
		},

		/**
		 * @function 	scrolling
		 * @desc 		Returns true if the
		 */
		scrolling() {
			return this.$store.state.scrolling
		}
	},

	watch: {
		scrolling(scrollState, lastScrollState) {
			if (scrollState && !lastScrollState && this.tooltipDisplay !== hiddenTooltip) {
				this.hideTooltip(0);
			}
		}
	},

	methods: {
		// Import utility functions
		formatDate,
		formatDateTime,

		/**
		 * @function 	centerScaleX
		 * @desc 		Converts the index of a bin to the x position on the chart where it meets the legend line.
		 * @return 		{Number} x position in pixels
		 */
		centerScaleX(x) {
			return (x + 0.5) * this.width / (this.bins.length + 1)
		},

		/**
		 * @function 	showTooltip
		 * @desc 		Display the given bin in the hovering tooltip (see guide for implementing the tooltip).
		 */
		showTooltip(bin, e) {
			if (Array.isArray(bin) && e &&
				(this.tooltipDisplay == hiddenTooltip || bin != this.currentTooltipBin)) {
				this.currentTooltipBin = bin;
				this.tooltipBin = bin.slice().sort((a, b) => {
					if (a.time)
						return a.time - b.time
					else
						return a - b
				});

				var image = e.srcElement;
				var offset = getOffset(image.parentNode);

				this.tooltipDisplay = {
					left: offset.left + 20,
					top: offset.top,
					width: 200,
					display: 'block'
				};
			}

		},

		hoverTooltip(mouseover) {
			if (mouseover) {
				clearTimeout(this.tooltipHideTimeout)
			}
			else {
				this.tooltipHideTimeout = setTimeout(() => {
					this.tooltipBin = emptyTooltip;
					this.currentTooltipBin = null;
					this.tooltipDisplay = hiddenTooltip;
				}, 400)
			}
		},

		/**
		 * @function 	hideTooltip
		 * @desc 		Hide the visible tooltip.
		 */
		hideTooltip(afterDelay) {
			if (this.tooltipDisplay != hiddenTooltip) {
				clearTimeout(this.tooltipHideTimeout);
				this.tooltipHideTimeout = setTimeout(() => {
					this.tooltipBin = emptyTooltip;
					this.currentTooltipBin = null;
					this.tooltipDisplay = hiddenTooltip;
				}, typeof afterDelay === 'number' ? afterDelay : 400)
			}
		},

		/**
		 * @function 	withPrecision
		 */
		withPrecision(value) {
			if (this.precision == 0 || Math.abs(value - Math.round(value)) < 0.01) {
				return Math.round(value)
			}
			else {
				return value.toFixed(this.precision)
			}
		}
	},

	components: {
		Legend,
		GraphPoint,
		Tooltip
	}
}
